import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { cacheConfig, QueryKeys } from "./constants";
import { mapProducts } from "../utils/productListMapping";
import { WishlistEndPoints } from "../constants/endpoints";
import { fetchWishlistData, updateWishlistData } from "../api/wishlistApi";

type BackendProductArray = Parameters<typeof mapProducts>[0];

export type WishlistResponse = {
  products: BackendProductArray;
};

export const useWishlist = (customerId: string) => {
  return useQuery<WishlistResponse>({
    queryKey: [QueryKeys.Wishlist],
    queryFn: () =>
      fetchWishlistData(WishlistEndPoints.getWishlistProducts(customerId)),
    enabled: !!customerId,
    ...cacheConfig.oneHour,
  });
};

export const useToggleWishlist = (customerId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) =>
      updateWishlistData({ customerId, productId }),

    onMutate: async (productId: string) => {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.Wishlist] });

      const previousWishlist = queryClient.getQueryData<WishlistResponse>([
        QueryKeys.Wishlist,
      ]);

      queryClient.setQueryData<WishlistResponse>(
        [QueryKeys.Wishlist],
        (currentWishlist) => {
          if (!currentWishlist?.products) return currentWishlist;

          const isProductInWishlist = currentWishlist.products.some(
            (product) => String(product.id) === String(productId)
          );

          if (isProductInWishlist) {
            return {
              products: currentWishlist.products.filter(
                (product) => String(product.id) !== String(productId)
              ),
            };
          }

          return currentWishlist;
        }
      );

      return { previousWishlist };
    },

    onError: (_error, _productId, context) => {
      if (context?.previousWishlist) {
        queryClient.setQueryData(
          [QueryKeys.Wishlist],
          context.previousWishlist
        );
      }
    },

    onSuccess: (updatedWishlist) => {
      if (updatedWishlist?.products?.length) {
        queryClient.setQueryData(
          [QueryKeys.Wishlist],
          updatedWishlist as WishlistResponse
        );
      } else {
        queryClient.invalidateQueries({ queryKey: [QueryKeys.Wishlist] });
      }
    },
  });
};
